import { guardString } from "../strings";
import type { Nullable } from "../types";
import { getLargeNumberString } from "./get-large-number-string";
import { guardNumber } from "./guard-number";

export type NumberFormatVariant =
	| "currency"
	| "millimeters"
	| "centimeters"
	| "meters"
	| "meters-week"
	| "kilometers"
	| "gram"
	| "day"
	| "kilogram"
	| "ton"
	| "decimal"
	| "integer"
	| "hour"
	| "cubic-meters"
	| "square-meters"
	| "square-meters-week"
	| "piece-week"
	| "percent"
	| "thousands";

function decimal(props?: Intl.NumberFormatOptions) {
	return Object.assign({ minimumFractionDigits: 2, maximumFractionDigits: 2 }, props);
}

function integer(props?: Intl.NumberFormatOptions) {
	return Object.assign({ minimumFractionDigits: 0, maximumFractionDigits: 0 }, props);
}

const NumberFormatVariants: Record<NumberFormatVariant, Intl.NumberFormatOptions> = {
	currency: decimal({
		style: "currency",
		currency: "EUR",
	}),
	millimeters: decimal({
		style: "unit",
		unit: "millimeter",
	}),
	centimeters: decimal({
		style: "unit",
		unit: "centimeter",
	}),
	meters: decimal({
		style: "unit",
		unit: "meter",
	}),
	kilometers: decimal({
		style: "unit",
		unit: "kilometer",
	}),
	gram: decimal({
		style: "unit",
		unit: "gram",
	}),
	kilogram: decimal({
		style: "unit",
		unit: "kilogram",
	}),
	ton: decimal(),
	hour: decimal({
		style: "unit",
		unit: "hour",
	}),
	percent: integer({
		style: "percent",
	}),
	decimal: decimal({ style: "decimal" }),
	integer: integer(),
	"cubic-meters": decimal(),
	"square-meters": decimal(),
	"square-meters-week": decimal(),
	"meters-week": decimal({ style: "unit", unit: "meter" }),
	"piece-week": decimal(),
	thousands: integer(),
	day: {
		style: "unit",
		unit: "day",
		unitDisplay: "long",
		maximumFractionDigits: 1,
	},
};

/**
 * This function is used to format a number to a given variant.
 */
export function formatNumber(
	_input: Nullable<string | number>,
	_variant?: Nullable<NumberFormatVariant>,
	fallback: string | number = "--",
): string {
	if ((_input === null || _input === undefined) && !_variant) return guardString(null);
	if (_input === 0 && !["thousands", "day"].includes(_variant!)) return `${fallback}`;
	if (Number.isNaN(_input as number)) return `${fallback}`;
	let input = guardNumber(_input);
	const variant = _variant || "decimal";
	if (variant === "percent") input = input / 100;
	const result = input.toLocaleString("de-DE", NumberFormatVariants[variant]);
	if (variant === "ton") return `${result} t`;
	if (variant === "cubic-meters") return `${result} m³`;
	if (variant === "square-meters") return `${result} m²`;
	if (variant === "square-meters-week") return `${result} m²/Wo.`;
	if (variant === "piece-week") return `${result} Stk./Wo.`;
	if (variant === "thousands") {
		return getLargeNumberString(input);
	}
	return result;
}

export function getNumberFormatOptions(variant: Nullable<NumberFormatVariant>) {
	return NumberFormatVariants[variant || "decimal"] || {};
}
